// frontend/src/contexts/AccessibilityContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'a11y_settings';

const DEFAULTS = {
  highContrast: false,
  largeFont: false,
  textOnly: false,
};

const AccessibilityContext = createContext(null);

function loadInitialSettings() {
  // Saved preference wins.
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return { ...DEFAULTS, ...JSON.parse(raw) };
    }
  } catch (e) {
    // ignore malformed/blocked storage
  }
  // Otherwise seed high-contrast from the OS preference (best-effort).
  try {
    if (window.matchMedia && window.matchMedia('(prefers-contrast: more)').matches) {
      return { ...DEFAULTS, highContrast: true };
    }
  } catch (e) {
    // matchMedia unsupported
  }
  return { ...DEFAULTS };
}

export function AccessibilityProvider({ children }) {
  const [settings, setSettings] = useState(loadInitialSettings);

  // Apply settings as classes on <html> so they cascade to the whole app
  // (including Bootstrap surfaces via accessibility.css), and persist them.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('high-contrast', !!settings.highContrast);
    root.classList.toggle('large-font', !!settings.largeFont);
    root.classList.toggle('text-only', !!settings.textOnly);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      // storage may be unavailable (private mode); settings still apply for the session
    }
  }, [settings]);

  const setSetting = useCallback((name, value) => {
    setSettings((prev) => ({ ...prev, [name]: value }));
  }, []);

  const toggle = useCallback((name) => {
    setSettings((prev) => ({ ...prev, [name]: !prev[name] }));
  }, []);

  return (
    <AccessibilityContext.Provider value={{ ...settings, setSetting, toggle }}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return ctx;
}

export default AccessibilityContext;
